/* eslint-disable react-hooks/exhaustive-deps */
import { HeartFilled, HeartOutlined } from '@ant-design/icons';
import { Badge, Button, Card, List, Popover, Tag, Typography } from 'antd';
import clsx from 'clsx';
import { BadgePoundSterling, ShoppingCart } from 'lucide-react';
import { Activity, memo, useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ReactComponent as TaxableIcon } from '../../../assets/svg/Tax.svg';
import { buttonStates, PLACEHOLDER_IMAGE } from '../../../constants';
import { addOrderItem, removeOrderItem } from '../../../redux/actions/order';
import { getProductDetails } from '../../../redux/actions/products';
import { getRoutes } from '../../../routes/Routes';
import { formatCurrency, getProductImageUrl } from '../../../utils';
import { errorToast } from '../../../utils/toast';
import QuantityInput from '../QuantityInput';
import _ from 'lodash';

const { Title, Text } = Typography;

const ProductCard = ({
    product,
    onFavToggle,
    onCreateOrder,
    showFav,
    showQTY = true,
    openOrderId,
    isPriceLevels = false,
    isRemoveProduct = false,
}) => {
    const { orderId: paramOrderId } = useParams();
    const [quantity, setQuantity] = useState(product?.quantity || 1);
    const [loading, setLoading] = useState(false);
    const [imgLoaded, setImgLoaded] = useState(false);
    const [priceLevel, setPriceLevel] = useState(null);
    const [loadingPriceLevels, setLoadingPriceLevels] = useState(false);

    const orderId = paramOrderId || openOrderId;
    const isOffer = useMemo(
        () => product?.promo_price && product?.promo_price <= product?.unit_price,
        [product]
    );
    const isAddedToOrder = !!product?.id;
    const isFavourite = !!product?.favourite;
    const productImage = useMemo(() => getProductImageUrl(product), [product?.id, product?.image]);

    const stateKey = () => {
        if (quantity === 0) return 'remove';
        if (isAddedToOrder) return 'update';
        return 'add';
    };

    const getPriceLevel = useCallback(async (id) => {
        try {
            setLoadingPriceLevels(true);
            const res = await getProductDetails({ id, details: 1, loading: false });
            if (res?.ack) setPriceLevel(res?.product);
        } finally {
            setLoadingPriceLevels(false);
        }
    }, []);


    const handleAddToOrder = async () => {
        if (loading) return;
        setLoading(true);
        if (isOffer) product.unit_price = product.promo_price;

        try {
            const body = {
                id: product?.id,
                order_id: orderId,
                product_netsuite_id: product?.netsuite_id,
                quantity,
                unit_price: product?.unit_price,
            };

            if (!orderId) {
                onCreateOrder?.(body, product);
                return;
            }

            if (isAddedToOrder && quantity <= 0) {
                const res = await removeOrderItem(product?.id, orderId);
                if (res?.ack) setQuantity(1);
            } else if (quantity > 0) {
                await addOrderItem(body, product);
            } else {
                errorToast('Please select a valid quantity.');
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const priceListContent = useMemo(() => {
        const sortedPriceLevels = _.sortBy(priceLevel || [], (item) => {
            const match = item.price_level.match(/^(\d+)/);
            if (match) return parseInt(match[1], 10);
            return Infinity;
        });

        const numbered = sortedPriceLevels.filter((item) => /^\d+/.test(item.price_level));
        const unnumbered = sortedPriceLevels.filter((item) => !/^\d+/.test(item.price_level));
        const finalSorted = [...numbered, ..._.sortBy(unnumbered, ['price_level'])];

        return (
            <List
                size="small"
                loading={loadingPriceLevels}
                dataSource={finalSorted}
                renderItem={(item) => (
                    <List.Item>
                        <span style={{ fontWeight: 600, minWidth: 70 }}>{item.price_level}:</span>
                        <span style={{ marginLeft: 8 }}>{formatCurrency(item.unit_price)}</span>
                    </List.Item>
                )}
            />
        );
    }, [loadingPriceLevels, priceLevel]);

    const CardContent = (
        <Card
            hoverable
            className={clsx('product-card', { added: isAddedToOrder, offer: isOffer })}
            classNames={{
                body: 'p-2',
            }}
            cover={
                <Link to={getRoutes().catalogue_details.route(product?.netsuite_id)}>
                    <div className="product-cover">
                        <img
                            className={clsx({
                                'broken-img': !productImage,
                                'fade-in': imgLoaded,
                            })}
                            loading="lazy"
                            draggable={false}
                            alt={product?.name}
                            src={productImage || PLACEHOLDER_IMAGE}
                            onLoad={() => setImgLoaded(true)}
                        />
                        <Activity mode={showFav ? 'visible' : 'hidden'}>
                            <Button
                                className="favorite-btn shadow-2"
                                color="pink"
                                variant={isFavourite ? 'solid' : 'filled'}
                                shape="round"
                                icon={isFavourite ? <HeartFilled /> : <HeartOutlined />}
                                onClick={(e) => {
                                    e.preventDefault();
                                    onFavToggle?.(product);
                                }}
                            />
                        </Activity>
                        {isAddedToOrder && <div className="added-badge">✓</div>}
                        <Tag color="#00AFA5" className="shadow-3 text-lg">
                            {product?.code}
                        </Tag>
                        {product?.taxable === 'Taxable' && (
                            <div className="taxable flex justify-content-center align-items-bottom">
                                <TaxableIcon width={40} height={40} />
                            </div>
                        )}
                    </div>
                </Link>
            }
        >
            <div className="flex justify-content-between align-items-center mb-2">
                <Tag color="blue" className="truncate max-w-full" title={product?.sub_category}>
                    {product?.sub_category}
                </Tag>
            </div>

            <Title level={5} className="m-0 text-sm" ellipsis={{ tooltip: true }}>
                {product?.name}
            </Title>

            <div className="flex justify-content-between align-items-center mb-2">
                <div className="flex items-center gap-2">
                    {isOffer ? (
                        <>
                            <Title
                                level={5}
                                className="m-0 font-bold"
                                style={{
                                    color: 'red',
                                }}
                            >
                                {formatCurrency(product?.promo_price)}
                            </Title>
                            <Text delete type="secondary" className="">
                                {formatCurrency(product?.unit_price)}
                            </Text>
                        </>
                    ) : (
                        <Title level={5} className="m-0" style={{ fontWeight: 700 }}>
                            {formatCurrency(product?.unit_price)}
                        </Title>
                    )}
                </div>

                <Activity mode={isPriceLevels ? 'visible' : 'hidden'}>
                    <Popover
                        title="Price Levels"
                        content={priceListContent}
                        trigger="click"
                        classNames={{
                            body: 'max-h-20rem overflow-y-auto',
                        }}
                    >
                        <BadgePoundSterling
                            size={22}
                            cursor="pointer"
                            className='price-level'
                            onClick={() => getPriceLevel(product?.netsuite_id)}
                        />
                    </Popover>
                </Activity>
            </div>

            <Activity mode={showQTY ? 'visible' : 'hidden'}>
                <QuantityInput
                    value={quantity}
                    onChange={setQuantity}
                    min={isRemoveProduct ? 0 : 1}
                    className="mb-1"
                    size="small"
                />
            </Activity>

            <Button
                block
                icon={<ShoppingCart size={16} />}
                loading={loading}
                onClick={handleAddToOrder}
                size="small"
                color={buttonStates[stateKey()].color}
                variant="solid"
            >
                {buttonStates[stateKey()].label}
            </Button>
        </Card>
    );

    return isOffer ? (
        <Badge.Ribbon text="Offer" placement="start" color="red">
            {CardContent}
        </Badge.Ribbon>
    ) : (
        CardContent
    );
};

export default memo(ProductCard);
